/**
 * @file 主菜单（React 外壳）—— 单机对战 / 联机好友房 / 设置。
 */

import { useState } from 'react';
import type { AIDifficulty } from '@majiang/core';
import type { OnlineConnInfo } from '../session';

export interface MenuScreenProps {
  readonly onStartLocal: (difficulty: AIDifficulty, rounds: 1 | 8 | 16) => void;
  readonly onEnterRoom: () => void;
  readonly savedConn: OnlineConnInfo | null;
  readonly onResume: (conn: OnlineConnInfo) => void;
}

const DIFFICULTIES: readonly { readonly value: AIDifficulty; readonly label: string; readonly desc: string }[] = [
  { value: 'EASY', label: '简单', desc: '新手休闲' },
  { value: 'NORMAL', label: '普通', desc: '默认推荐' },
  { value: 'HARD', label: '困难', desc: '高手对局' },
];

const ROUND_OPTIONS: readonly { readonly value: 1 | 8 | 16; readonly label: string; readonly desc: string }[] = [
  { value: 1, label: '1 局', desc: '单局结算' },
  { value: 8, label: '8 局', desc: '多局累计' },
  { value: 16, label: '16 局', desc: '完整对决' },
];

export function MenuScreen({ onStartLocal, onEnterRoom, savedConn, onResume }: MenuScreenProps): React.JSX.Element {
  const [diff, setDiff] = useState<AIDifficulty>('NORMAL');
  const [rounds, setRounds] = useState<1 | 8 | 16>(1);
  return (
    <main className="shell">
      <div className="card">
        <h1>四川麻将 · 血战到底</h1>
        <p className="subtitle">定缺 · 刮风下雨 · 查大叫 · 一炮多响</p>
        <div className="divider" />

        <h2>单机对战</h2>
        <div className="row">
          {DIFFICULTIES.map((d) => (
            <button
              key={d.value}
              className={diff === d.value ? 'btn active' : 'btn ghost'}
              onClick={() => setDiff(d.value)}
            >
              {d.label}
            </button>
          ))}
        </div>
        <div className="row">
          {ROUND_OPTIONS.map((r) => (
            <button
              key={r.value}
              className={rounds === r.value ? 'btn active' : 'btn ghost'}
              onClick={() => setRounds(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button className="btn primary" onClick={() => onStartLocal(diff, rounds)} style={{ marginTop: '0.5rem' }}>
          开始对局（{DIFFICULTIES.find((d) => d.value === diff)?.label} · {ROUND_OPTIONS.find((r) => r.value === rounds)?.label}）
        </button>
        <p className="hint">你坐 0 号位，三家 AI；多局制每局结束可复盘，确认后进入下一局</p>

        <div className="divider" />

        <h2>联机好友房</h2>
        <button className="btn" onClick={onEnterRoom}>
          建房 / 入座
        </button>
        <p className="hint">WebSocket 直连 M3 服务端，房主满四人后开局（联机为单局制）</p>

        {savedConn !== null && (
          <>
            <div className="divider" />
            <button className="btn ghost" onClick={() => onResume(savedConn)}>
              继续上一局（{savedConn.roomCode}）
            </button>
          </>
        )}

        <div className="divider" />
        <h2>设置</h2>
        <div>
          <label className="label">定缺开关</label>
          <input type="checkbox" checked disabled readOnly />
          <span className="hint" style={{ marginLeft: '0.5rem' }}>
            强制开启 —— M1 规则集锁死定缺（GDD 07 V-8 未实现护栏，`noQueMode` 不可启用）
          </span>
        </div>
        <p className="hint" style={{ textAlign: 'center' }}>
          M4-T2 · React 19 + PixiJS 8.19 + Vite 7
        </p>
      </div>
    </main>
  );
}
